/**
 * Transfer Detail Drawer Component
 *
 * Orchestrator drawer showing transfer details with actions.
 * Delegates rendering to sub-components and logic to useTransferDrawerState.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see US-14.6 - UI Detail et Reception
 * @see Sprint 22 - Transferts par Packaging
 */

import { useEffect } from 'react';
import { Drawer, Skeleton } from '@/components/ui';
import { useTransfer } from '../../hooks';
import { useTransferDrawerState } from './useTransferDrawerState';
import { useAuth } from '@/context/AuthContext';
import { useBrandContext, useCurrency } from '@/hooks';
import { TransferHeader } from './TransferHeader';
import { TransferLinesTable } from './TransferLinesTable';
import { TransferEditForm } from './TransferEditForm';
import { TransferShipForm } from './TransferShipForm';
import { TransferReceiveForm } from './TransferReceiveForm';
import { TransferCancelForm } from './TransferCancelForm';
import { TransferActions } from './TransferActions';
import { DocumentSection } from '@/features/documents/components/DocumentSection';

interface TransferDetailDrawerProps {
  transferId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canShip?: boolean;
  canReceive?: boolean;
  canDelete?: boolean;
}

export function TransferDetailDrawer({
  transferId,
  open,
  onOpenChange,
  canShip: hasShipPermission = true,
  canReceive: hasReceivePermission = true,
  canDelete: hasDeletePermission = true,
}: TransferDetailDrawerProps) {
  useAuth(); // Ensure user is authenticated
  const { isSite, isCentral, brandId } = useBrandContext();
  const currency = useCurrency();
  const currentSiteId = (isSite || isCentral) ? (brandId ?? '') : '';

  const { data: transfer, isLoading } = useTransfer(transferId);
  const packagingOptionsMap = transfer?.packagingOptionsMap ?? {};

  const state = useTransferDrawerState({
    transfer,
    currentSiteId,
    packagingOptionsMap,
    hasShipPermission,
    hasReceivePermission,
    hasDeletePermission,
    onClose: () => onOpenChange(false),
  });

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) state.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={transfer?.transferNumber ?? 'Chargement...'}
      size="xlarge"
    >
      {isLoading || !transfer ? (
        <div className="space-y-4">
          <Skeleton className="h-8" />
          <Skeleton className="h-24" />
          <Skeleton className="h-48" />
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Scrollable content */}
          <div className="flex-1 overflow-auto">
            {/* Warning if user is on a group, not a site */}
            {!isSite && !isCentral && (
              <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <p className="text-sm text-warning-700">
                  Vous etes connecte a un groupe. Pour effectuer des actions sur ce transfert,
                  basculez vers un site via le selecteur en haut de la sidebar.
                </p>
              </div>
            )}

            {/* Header info */}
            <TransferHeader
              transfer={transfer}
              isDestination={state.isDestination}
              currency={currency}
            />

            {/* Lines */}
            <div className="py-6">
              <h3 className="text-sm font-medium text-neutral-700 mb-3">
                Produits ({state.mode === 'edit' ? state.editLines.length : transfer.lines.length})
              </h3>

              {state.mode === 'edit' ? (
                <TransferEditForm
                  editLines={state.editLines}
                  editNotes={state.editNotes}
                  onEditNotesChange={state.setEditNotes}
                  productSearch={state.productSearch}
                  onProductSearchChange={state.setProductSearch}
                  productOptions={state.productOptions}
                  searchingProducts={state.searchingProducts}
                  onAddLine={state.handleAddEditLine}
                  onRemoveLine={state.handleRemoveEditLine}
                  onQuantityChange={state.handleEditQuantityChange}
                  onPackagingChange={state.handleEditPackagingChange}
                  onPackagingQtyChange={state.handleEditPackagingQtyChange}
                />
              ) : state.mode === 'ship' ? (
                <TransferShipForm
                  transfer={transfer}
                  shippedQuantities={state.shippedQuantities}
                  packagingOptionsMap={packagingOptionsMap}
                  onPackagingChange={state.handleShipPackagingChange}
                  onPackagingQtyChange={state.handleShipPackagingQtyChange}
                  onBaseQtyChange={state.handleShipBaseQtyChange}
                  shipDate={state.shipDate}
                  onShipDateChange={state.setShipDate}
                />
              ) : state.mode === 'receive' ? (
                <TransferReceiveForm
                  transfer={transfer}
                  receivedQuantities={state.receivedQuantities}
                  packagingOptionsMap={packagingOptionsMap}
                  onPackagingChange={state.handleReceivePackagingChange}
                  onPackagingQtyChange={state.handleReceivePackagingQtyChange}
                  onBaseQtyChange={state.handleReceiveBaseQtyChange}
                  onReasonChange={state.handleReceiveReasonChange}
                  receiveDate={state.receiveDate}
                  onReceiveDateChange={state.setReceiveDate}
                />
              ) : (
                <TransferLinesTable
                  lines={transfer.lines}
                  currency={currency}
                />
              )}
            </div>

            {/* Documents — visible sauf DRAFT */}
            {transfer.status !== 'DRAFT' && state.mode === 'view' && (
              <DocumentSection
                entityRoute="transfers"
                entityId={transfer.id}
              />
            )}

            {/* Cancel form */}
            {state.mode === 'cancel' && (
              <TransferCancelForm
                cancelReason={state.cancelReason}
                onReasonChange={state.setCancelReason}
                onConfirm={state.handleCancel}
                onBack={state.cancelMode}
                isPending={state.cancelMutation.isPending}
              />
            )}
          </div>

          {/* Actions — sticky en bas */}
          <TransferActions
            mode={state.mode}
            canSubmit={state.canSubmit}
            canShip={state.canShip}
            canReceive={state.canReceive}
            canCancel={state.canCancel}
            canEdit={state.canEdit}
            editLinesCount={state.editLines.length}
            onSaveEdit={state.handleSaveEdit}
            onCancelEditing={state.cancelEditing}
            isUpdatePending={state.updateMutation.isPending}
            onShip={state.handleShip}
            onCancelShipping={state.cancelMode}
            isShipPending={state.shipMutation.isPending}
            onReceive={state.handleReceive}
            onCancelReceiving={state.cancelMode}
            isReceivePending={state.receiveMutation.isPending}
            onStartCancel={state.startCancel}
            onStartEditing={state.initEditing}
            onSubmit={state.handleSubmit}
            isSubmitPending={state.submitMutation.isPending}
            onStartShipping={state.initShipping}
            onStartReceiving={state.initReceiving}
          />
        </div>
      )}
    </Drawer>
  );
}
